/**
 * lazyScreen — wraps a screen factory in React.lazy + Suspense with a
 * screen-group skeleton fallback.
 *
 * Seven screen-group skeleton kinds map to compositions of the v.1-E
 * SkeletonText, SkeletonHeading, SkeletonCard, SkeletonRow, and
 * SkeletonKpiTile primitives. Three additional primitive-level kinds
 * (row, card, text) render the corresponding skeleton directly.
 *
 * @see specs/prd.md § 7A — lazyScreen reference implementation
 * @see FR-001
 */
import React, { Suspense, type ComponentType, type ReactElement } from "react";
import {
  SkeletonRow,
  SkeletonHeading,
  SkeletonCard,
  SkeletonText,
  SkeletonKpiTile,
} from "../components/primitives/skeletons";
import { SectionContainer } from "../components/layout/SectionContainer";

// ---------------------------------------------------------------------------
// Skeleton kinds
// ---------------------------------------------------------------------------

/**
 * Screen-group skeleton kinds (PRD § 7A) plus primitive-level fallbacks.
 *
 * Screen-group kinds compose multiple skeleton primitives within a
 * SectionContainer; primitive kinds render a single skeleton component.
 */
type SkeletonKind =
  | "list"
  | "detail"
  | "dashboard"
  | "form"
  | "visualisation"
  | "trace"
  | "none"
  // Primitive-level kinds (used by tests and simple screens)
  | "row"
  | "card"
  | "text";

interface LazyScreenOptions {
  /** The skeleton kind to render during the pending import. Default: "list". */
  skeleton?: SkeletonKind;
}

// ---------------------------------------------------------------------------
// Skeleton compositions
// ---------------------------------------------------------------------------

const SKELETON_MAP: Record<SkeletonKind, () => ReactElement> = {
  // Screen-group compositions (PRD § 7A)
  list: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h4" width="40%" />
      <SkeletonRow count={8} />
    </SectionContainer>
  ),
  detail: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h2" width="60%" />
      <SkeletonRow count={6} />
    </SectionContainer>
  ),
  dashboard: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h4" width="40%" />
      <SkeletonKpiTile />
      <SkeletonKpiTile />
      <SkeletonKpiTile />
      <SkeletonCard height="240px" />
      <SkeletonRow count={5} />
    </SectionContainer>
  ),
  form: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h4" width="40%" />
      <SkeletonRow count={4} />
    </SectionContainer>
  ),
  visualisation: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h4" width="40%" />
      <SkeletonCard height="400px" />
    </SectionContainer>
  ),
  trace: () => (
    <SectionContainer variant="editorial">
      <SkeletonHeading level="h4" width="40%" />
      <SkeletonRow count={20} />
    </SectionContainer>
  ),
  none: () => <></>,

  // Primitive-level fallbacks
  row: () => <SkeletonRow count={5} />,
  card: () => <SkeletonCard />,
  text: () => <SkeletonText lines={3} />,
};

// ---------------------------------------------------------------------------
// lazyScreen wrapper
// ---------------------------------------------------------------------------

/**
 * Wrap a screen factory in `React.lazy` + `Suspense` with a skeleton fallback.
 *
 * @param factory — dynamic import factory returning `{ default: Component }`
 * @param options — skeleton kind selection (default: `{ skeleton: "list" }`)
 * @returns A wrapper component that renders the skeleton during the pending
 *          import and the resolved component once available.
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- React.lazy requires ComponentType<any> */
export function lazyScreen<P extends Record<string, unknown> = Record<string, unknown>>(
  factory: () => Promise<{ default: ComponentType<any> }>,
  options: LazyScreenOptions = {},
): ComponentType<P> {
  const { skeleton = "list" } = options;
  const LazyComponent = React.lazy(factory);
  const Fallback = SKELETON_MAP[skeleton];

  function LazyWrapper(props: P): ReactElement {
    return (
      <Suspense fallback={<Fallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  }
  LazyWrapper.displayName = `Lazy(${skeleton})`;

  return LazyWrapper;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
